import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiDelete , planningPath } from '../services/PlanningApiClient';
import { UserRole } from '../types/User';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../hooks/useRole';
import Icon from './Icon';
import ConfirmDialog from './ui/ConfirmDialog';
import SearchableSelect from './ui/SearchableSelect';
import EmptyState from './EmptyState';
import LoadErrorBox from './ui/LoadErrorBox';
import { errorMessageOf, isApiError } from '../utils/apiErrors';
import { genericMatch } from '../utils/searchMatch';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { ROLE_BADGE_CLASS, ROLE_DESCRIPTIONS, ROLE_LABELS, ROLE_ORDER } from '../utils/roles';

/**
 * A person in the workspace. Roles are per workspace, not per project: one
 * role covers every project. `id` is a uuid and is how the API is addressed,
 * because one converted account has no e-mail address.
 */
interface PlanUser {
  id: string;
  email: string | null;
  name: string | null;
  role: UserRole;
  financially_blind?: boolean;
  has_password?: boolean;
  created_at: string;
  last_login_at: string | null;
}

const NO_EMAIL = 'No e-mail on file';

function getInitials(name: string | null, email: string | null): string {
  const src = name || email || '?';
  return src.split(/[\s@.]+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function formatLastLogin(last_login_at: string | null): string {
  if (!last_login_at) return 'Never logged in';
  const diff = Date.now() - new Date(last_login_at).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

interface UserManagementProps {
  onChanged?: () => void;
  /** The page's Invite button raises this counter; the invite form scrolls into view and takes focus when it goes up. */
  inviteSignal?: number;
}

const UserManagement: React.FC<UserManagementProps> = ({ onChanged, inviteSignal }) => {
  const { user: currentUser } = useAuth();
  // Only an owner can invite, change or remove people (the API refuses everyone else).
  const { isOwner } = useRole();
  const [users, setUsers] = useState<PlanUser[]>([]);
  // The header search narrows the logins.
  const search = useHeaderSearch().trim().toLowerCase();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // A failed save, resend or removal: said in plain words, apart from a failed load.
  const [actionError, setActionError] = useState<string | null>(null);

  const inviteCardRef = useRef<HTMLDivElement>(null);
  const inviteEmailRef = useRef<HTMLInputElement>(null);
  const lastInviteSignal = useRef(inviteSignal ?? 0);
  // An Invite click that arrives while the list is still loading (the form is not on screen yet) is kept and applied once it mounts.
  const pendingInvite = useRef(false);
  const showInviteForm = () => {
    if (!inviteCardRef.current) return false;
    inviteCardRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    inviteEmailRef.current?.focus();
    return true;
  };
  useEffect(() => {
    if (inviteSignal !== undefined && inviteSignal > lastInviteSignal.current && !showInviteForm()) {
      pendingInvite.current = true;
    }
    lastInviteSignal.current = inviteSignal ?? 0;
  }, [inviteSignal]);
  useEffect(() => {
    if (pendingInvite.current && showInviteForm()) pendingInvite.current = false;
  }, [loading, isOwner]);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('planner');
  const [inviteFinanciallyBlind, setInviteFinanciallyBlind] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const shownUsers = useMemo(
    // A row being edited stays, so it does not vanish under the person typing into it.
    () => (search
      ? users.filter(u => u.id === editingId || genericMatch({ name: u.name, email: u.email, role: ROLE_LABELS[u.role] }, search))
      : users),
    [users, search, editingId],
  );
  const [editingRole, setEditingRole] = useState<UserRole>('planner');
  const [editingFinanciallyBlind, setEditingFinanciallyBlind] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [editingNewEmail, setEditingNewEmail] = useState('');
  const [editingPassword, setEditingPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const [removingId, setRemovingId] = useState<string | null>(null);
  const [sendingWelcome, setSendingWelcome] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PlanUser | null>(null);
  const [welcomeSent, setWelcomeSent] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ data: PlanUser[] }>(planningPath('/users'));
      setUsers(res.data);
    } catch (e) {
      // 404 means the endpoint isn't deployed yet — treat as empty, not an error
      if (!(isApiError(e) && e.status === 404)) {
        setError(e ?? new Error('load failed'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      await apiPost(planningPath('/users'), {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim() || null,
        role: inviteRole,
        financially_blind: inviteRole === 'viewer' ? inviteFinanciallyBlind : false,
      });
      setInviteSuccess(`${inviteEmail.trim()} added as ${ROLE_LABELS[inviteRole]}.`);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('planner');
      setInviteFinanciallyBlind(false);
      await load();
      onChanged?.();
    } catch (e) {
      setInviteError(errorMessageOf(e, "Couldn't add this person. Please try again."));
    } finally {
      setInviting(false);
    }
  };

  const startEdit = (user: PlanUser) => {
    setEditingId(user.id);
    setEditingRole(user.role);
    setEditingFinanciallyBlind(user.financially_blind === true);
    setEditingName(user.name ?? '');
    setEditingNewEmail(user.email ?? '');
    setEditingPassword('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingPassword('');
  };

  const saveUser = async (target: PlanUser) => {
    setSaving(true);
    setActionError(null);
    try {
      const body: Record<string, string | boolean | null> = {
        role: editingRole,
        // A contractor is blind by role already; the flag is only for viewers.
        financially_blind: editingRole === 'viewer' ? editingFinanciallyBlind : false,
      };
      if (editingName !== (target.name ?? '')) {
        body.name = editingName || null;
      }
      const nextEmail = editingNewEmail.trim().toLowerCase();
      if (nextEmail && nextEmail !== (target.email ?? '').toLowerCase()) {
        body.new_email = nextEmail;
      }
      if (editingPassword) {
        body.password = editingPassword;
      }
      // By id, not e-mail: stable if the e-mail is being changed, and the only
      // handle for a person who has no e-mail yet.
      await apiPut(planningPath(`/users/${encodeURIComponent(target.id)}`), body);
      setEditingId(null);
      setEditingPassword('');
      await load();
      onChanged?.();
    } catch (e) {
      setActionError(errorMessageOf(e, "Couldn't save the changes. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleResendWelcome = async (target: PlanUser) => {
    setSendingWelcome(target.id);
    setActionError(null);
    try {
      await apiPost(planningPath(`/users/${encodeURIComponent(target.id)}/resend-welcome`), {});
      setWelcomeSent(prev => new Set([...prev, target.id]));
    } catch (e) {
      setActionError(errorMessageOf(e, "Couldn't send the welcome e-mail. Please try again."));
    } finally {
      setSendingWelcome(null);
    }
  };

  const confirmRemoveUser = async (target: PlanUser) => {
    setRemovingId(target.id);
    setActionError(null);
    try {
      await apiDelete(planningPath(`/users/${encodeURIComponent(target.id)}`));
      await load();
      onChanged?.();
    } catch (e) {
      setActionError(errorMessageOf(e, "Couldn't remove this person. Please try again."));
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="um-loading">
        <div className="loading-spinner" />
        <p>Loading team…</p>
      </div>
    );
  }

  return (
    <div className="um-root">
      {error != null && <LoadErrorBox what="team members" error={error} onRetry={load} retrying={loading} />}
      {actionError && <p className="form-error-text" role="alert">{actionError}</p>}

      {/* ── User list ── */}
      <div className="settings-card">
        <h4 className="settings-card-title">Logins</h4>
        <p className="settings-muted-xs">
          People with a login can sign in with Google or a password. Each login has one role, and it applies to
          every project in this workspace.
          {isOwner ? ' Invite someone by email below, or give a login to a person in Team and contacts above.' : ' Only an owner can add or change logins.'}
        </p>

        <div className="um-user-list">
          {shownUsers.map(u => {
            const isMe = !!u.email && u.email.toLowerCase() === (currentUser?.email ?? '').toLowerCase();
            const isEditing = editingId === u.id;

            return (
              <div key={u.id} className={`um-user-row${isEditing ? ' um-user-row--editing' : ''}`}>
                <div className="um-user-avatar">{getInitials(u.name, u.email)}</div>
                <div className="um-user-info">
                  <div className="um-user-name">
                    {u.name || u.email || NO_EMAIL}
                    {isMe && <span className="um-you-badge">you</span>}
                  </div>
                  <div className="um-user-email">{u.name ? (u.email ?? NO_EMAIL) : null}</div>
                  <div className={`um-last-login${!u.last_login_at ? ' um-last-login--never' : ''}`}>
                    <Icon name="clock" size={11} />
                    {formatLastLogin(u.last_login_at)}
                  </div>
                </div>

                {isEditing ? (
                  <div className="um-edit-panel">
                    <div className="um-edit-fields">
                      <div className="um-edit-field">
                        <label className="um-edit-label">Name</label>
                        <input
                          className="form-input form-input--sm"
                          type="text"
                          value={editingName}
                          onChange={e => setEditingName(e.target.value)}
                          placeholder="Display name"
                          autoFocus
                        />
                      </div>
                      <div className="um-edit-field">
                        <label className="um-edit-label">Email</label>
                        <input
                          className="form-input form-input--sm"
                          type="email"
                          value={editingNewEmail}
                          onChange={e => setEditingNewEmail(e.target.value)}
                          placeholder={u.email ? undefined : 'Add an e-mail so they can sign in'}
                        />
                      </div>
                      <div className="um-edit-field">
                        <label className="um-edit-label">New Password</label>
                        <input
                          className="form-input form-input--sm"
                          type="password"
                          value={editingPassword}
                          onChange={e => setEditingPassword(e.target.value)}
                          placeholder="Leave blank to keep current"
                        />
                      </div>
                      <div className="um-edit-field">
                        <label className="um-edit-label">Role</label>
                        <SearchableSelect
                          value={editingRole}
                          onChange={value => setEditingRole(value as UserRole)}
                          className="form-select form-select--sm"
                          options={ROLE_ORDER.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
                        />
                      </div>
                      {editingRole === 'viewer' && (
                        <div className="um-edit-field um-edit-field--checkbox">
                          <label className="um-checkbox-label">
                            <input
                              type="checkbox"
                              checked={editingFinanciallyBlind}
                              onChange={e => setEditingFinanciallyBlind(e.target.checked)}
                            />
                            Hide money amounts from this viewer
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="um-edit-actions">
                      <button
                        className="btn-primary um-save-btn"
                        onClick={() => saveUser(u)}
                        disabled={saving}
                      >
                        <Icon name={saving ? 'loader' : 'check'} size={14} />
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="um-action-btn" onClick={cancelEdit} title="Cancel">
                        <Icon name="close" size={14} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="um-user-actions">
                    <span className={`ds-badge ${ROLE_BADGE_CLASS[u.role] ?? 'ds-badge-neutral'}`}>
                      {ROLE_LABELS[u.role] ?? u.role}
                    </span>
                    {u.financially_blind && u.role !== 'contractor' && (
                      <span className="ds-badge ds-badge-neutral" title="Money amounts are hidden from this person">
                        <Icon name="lock" size={11} /> Financially blind
                      </span>
                    )}
                    {isOwner && !isMe && (
                      <>
                        {u.email && (welcomeSent.has(u.id) ? (
                          <span className="um-welcome-sent" title="Welcome email sent">
                            <Icon name="check" size={13} /> Sent
                          </span>
                        ) : (
                          <button
                            className={`um-action-btn um-action-btn--welcome${!u.last_login_at ? ' um-action-btn--welcome-pulse' : ''}`}
                            onClick={() => handleResendWelcome(u)}
                            disabled={sendingWelcome === u.id}
                            title={u.last_login_at ? 'Resend welcome email' : 'Send welcome email (never logged in)'}
                          >
                            <Icon name={sendingWelcome === u.id ? 'loader' : 'mail'} size={14} />
                          </button>
                        ))}
                        <button
                          className="um-action-btn"
                          onClick={() => startEdit(u)}
                          title="Edit user"
                        >
                          <Icon name="edit" size={14} />
                        </button>
                        <button
                          className="um-action-btn um-action-btn--danger"
                          onClick={() => setRemoveTarget(u)}
                          disabled={removingId === u.id}
                          title="Remove access"
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {users.length === 0 && (
            <EmptyState compact icon="users" title="No team members yet" />
          )}
          {users.length > 0 && shownUsers.length === 0 && (
            <EmptyState compact icon="users" title="No logins match your search" />
          )}
        </div>
      </div>

      {/* ── Role legend ── */}
      <div className="settings-card um-role-legend">
        <h4 className="settings-card-title">Role Permissions</h4>
        <div className="um-role-grid">
          {ROLE_ORDER.map(r => (
            <div key={r} className="um-role-item">
              <span className={`ds-badge ${ROLE_BADGE_CLASS[r]}`}>
                {ROLE_LABELS[r]}
              </span>
              <span className="um-role-desc">{ROLE_DESCRIPTIONS[r]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Invite form ── */}
      {isOwner && (
        <div className="settings-card" ref={inviteCardRef}>
          <h4 className="settings-card-title">Invite a login by email</h4>
          <form onSubmit={handleInvite} className="um-invite-form">
            <div className="um-invite-fields">
              <div className="form-group">
                <label>Email <span className="form-required">*</span></label>
                <input
                  ref={inviteEmailRef}
                  type="email"
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteError(null); setInviteSuccess(null); }}
                  placeholder="colleague@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Name (optional)</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={e => setInviteName(e.target.value)}
                  placeholder="Display name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <SearchableSelect
                  value={inviteRole}
                  onChange={value => setInviteRole(value as UserRole)}
                  options={ROLE_ORDER.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
                />
              </div>
            </div>
            <p className="form-hint">{ROLE_DESCRIPTIONS[inviteRole]}. The role covers every project in this workspace.</p>
            {inviteRole === 'viewer' && (
              <label className="um-checkbox-label">
                <input
                  type="checkbox"
                  checked={inviteFinanciallyBlind}
                  onChange={e => setInviteFinanciallyBlind(e.target.checked)}
                />
                Hide money amounts from this viewer
              </label>
            )}
            {inviteError && <p className="form-error-text">{inviteError}</p>}
            {inviteSuccess && <p className="um-form-success">{inviteSuccess}</p>}
            <button type="submit" className="btn-primary" disabled={inviting || !inviteEmail.trim()}>
              {inviting ? 'Adding…' : 'Add Member'}
            </button>
          </form>
        </div>
      )}
      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          const target = removeTarget!;
          setRemoveTarget(null);
          void confirmRemoveUser(target);
        }}
        title="Remove member?"
        description={`Remove ${removeTarget?.name || removeTarget?.email || 'this person'} from this workspace? They will lose access to every project in it immediately.`}
        confirmLabel="Remove"
      />
    </div>
  );
};

export default UserManagement;
